import {
    ChangeDetectorRef,
    Component,
    ElementRef,
    EventEmitter,
    Input,
    OnChanges,
    OnDestroy,
    OnInit,
    Output,
    SimpleChanges,
    ViewChild,
} from '@angular/core';
import { Question, QuestionResponse, Section } from '../../../../interfaces/questions.interface';
import { CustomerQuestionnaireService } from '../../../../services/customer-questionnaire.service';
import { HTMLInputEvent } from '../../../../../../interfaces/HTMLInputEvent';
import { ToastService } from '../../../../../../../common-modules/services/toast.service';
import { CommonQuestionnaireService } from '../../../../services/common-questionnaire.service';
import { CstQuestionnaireService } from '../../../../services/cst-questionnaire.service';
import { CifcReview } from '../../../../interfaces/cifc.interface';
import { Subscription } from 'rxjs/internal/Subscription';
import { Observable, Subject } from 'rxjs';
import * as _ from 'lodash';

@Component({
    selector: 'app-question-footer',
    templateUrl: './question-footer.component.html',
    styleUrls: ['./question-footer.component.scss'],
    standalone: false,
})
export class QuestionFooterComponent implements OnInit, OnChanges, OnDestroy {
    @ViewChild('uploadInputElement') fileBrowserInput: ElementRef;
    @Input() question: Question;
    @Input() questionIndex: number;
    @Input() showOk: boolean | undefined;
    @Output() questionMarked = new EventEmitter();

    public currentAttachment: { file: any };
    public sections: Section[];
    public maxSize = 25;
    public extensionsAllowed = ['.pdf', '.xls', '.xlsx', '.doc', '.docx', '.txt', '.zip', '.tiff', '.gif', '.png', '.jpg'].sort();
    public mimeTypesAllowed = [
        'application/pdf',
        'application/vnd.ms-excel',
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        'application/msword',
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        'text/plain',
        'application/zip',
        'application/x-zip-compressed',
        'image/tiff',
        'image/gif',
        'image/png',
        'image/jpeg',
    ];
    public currentQuestion: Question;
    public questionReview: CifcReview | undefined;
    public reviewInitialValue = false;

    public reviewSubscription: Subscription;
    public answerSavedSubscription: Subscription;
    public addReviewAsDraft: Subject<Question> = new Subject<Question>();

    constructor(
        public customerQuestionnaireService: CustomerQuestionnaireService,
        public commonQuestionnaireService: CommonQuestionnaireService,
        public cstQuestionnaireService: CstQuestionnaireService,
        private toastService: ToastService,
        public cdr: ChangeDetectorRef,
    ) {
        if (this.commonQuestionnaireService.createMode) {
            this.cstQuestionnaireService.isQuestionnaireLoaded().subscribe((cstQuestionnaireLoaded) => {
                if (cstQuestionnaireLoaded) {
                    this.sections = this.cstQuestionnaireService.sections;
                }
            });
        }

        if (!this.commonQuestionnaireService.createMode || this.commonQuestionnaireService.reviewMode) {
            this.customerQuestionnaireService.isQuestionnaireLoaded().subscribe((customerQuestionnaireLoaded) => {
                if (customerQuestionnaireLoaded) {
                    this.sections = this.customerQuestionnaireService.getSections();
                }
            });
        }
    }

    ngOnInit(): void {
        this.currentAttachment = { file: null };
        this.currentQuestion = this.question;
        this.currentAttachment = this.currentQuestion?.body?.attachement;

        this.reviewSubscription = this.commonQuestionnaireService.reviewAddedObs().subscribe((review: CifcReview) => {
            this.showOk = true;
            if (review?.question_id === this.question.id) {
                this.questionReview = review;
                this.reviewInitialValue = false;
            }
        });

        this.commonQuestionnaireService.cancelReviewObs().subscribe((val) => {
            if (val.id === this.question.id && this.commonQuestionnaireService.showReviewWarningMark(val as unknown as QuestionResponse)) {
                this.questionReview = val.cifc_review;
                this.reviewInitialValue = true;
                this.cdr.detectChanges();
            } else if (val.id === this.question.id) {
                this.questionReview = undefined;
            }
        });

        this.answerSavedSubscription = this.customerQuestionnaireService.answerSavedSubject.asObservable().subscribe((val) => {
            this.customerQuestionnaireService.isLoadingSubmit = false;
            this.cdr.detectChanges();
        });
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes && changes.showOk) {
            this.showOk = changes.showOk.currentValue;
        }
        if (changes && changes.questionIndex) {
            this.questionIndex = changes.questionIndex.currentValue;
        }
    }

    ngOnDestroy() {
        this.reviewSubscription?.unsubscribe();
        this.answerSavedSubscription?.unsubscribe();
    }

    public onAddReviewAsDrft(): Observable<Question> {
        return this.addReviewAsDraft.asObservable();
    }

    public onAttachmentUpload($event: Event): void {
        const htmlInputEvent = $event as HTMLInputEvent;
        const fileList = htmlInputEvent.target.files || null;

        if (!fileList?.length || fileList[0] === null) {
            /* no files have been selected to be uploaded */
            return;
        }

        const file = fileList[0];

        /* reset file input so that the (change) event will get triggered again next time */
        this.fileBrowserInput.nativeElement.value = '';
        this.addAttachmentToQuestion(file);
    }

    public markQuestionAsComplete() {
        const answerHasChanged = _.cloneDeep(this.question.answerChanged);
        if (this.commonQuestionnaireService.reviewMode) {
            /** -> if the question already have an unsolved review, then we need to load the existing review
             *  -> if the question doesn't have a registered review, then we should take into account the recent left review */
            this.commonQuestionnaireService.displayOkCifc[this.questionIndex] = false;
            this.question.cifc_review =
                (!this.question.cifc_review?.solved &&
                    !this.commonQuestionnaireService.getReviewFromMapByQuestionId(this.question.id)?.recent &&
                    this.question.cifc_review) ||
                this.commonQuestionnaireService.cifcReviewMapping[this.question.id ?? ''];

            /** remove from local registered reviews */
            this.commonQuestionnaireService.removeEntityFromReviewMapping(this.question.id ?? '');
            (this.question as unknown as QuestionResponse).body.accepted = false;
            (this.question as unknown as QuestionResponse).body.updated = false;
        }

        if (!this.commonQuestionnaireService.createMode && !this.commonQuestionnaireService.reviewMode) {
            (this.question as unknown as QuestionResponse).body.last_update = new Date().toString();
            const lastQuestion =
                this.customerQuestionnaireService.displayVersion === 'full'
                    ? this.customerQuestionnaireService.processedFullSections[
                          this.customerQuestionnaireService.processedFullSections.length - 1
                      ]
                    : this.customerQuestionnaireService.processedCondensedSections[
                          this.customerQuestionnaireService.processedCondensedSections.length - 1
                      ];
            this.customerQuestionnaireService.showCheckSubmit = this.question.id === lastQuestion.id;
        }
        this.question.answerChanged = answerHasChanged;
        this.questionMarked.emit(true);
        this.customerQuestionnaireService.markQuestionAsComplete(this.question);

        for (let s of this.customerQuestionnaireService.sections) {
            const index = s.questions.findIndex((x) => x.id === this.question.id);
            if (index > -1) {
                s.questions[index].cifc_review = _.cloneDeep(this.question.cifc_review);
                s.questions[index].body.answered = _.cloneDeep(this.question.body.answered);
                break;
            }
        }
        if (this.commonQuestionnaireService.reviewMode) {
            this.toastService.showSuccess('Review added to list.', { duration: 3000 });
        }

        setTimeout(() => {
            this.cdr.detectChanges();
        }, 2000);
    }

    public cancelReviewButton() {
        if (!this.question.id) {
            return;
        }

        this.commonQuestionnaireService.removeEntityFromReviewMapping(this.question.id);
        this.commonQuestionnaireService.resetCifcQuestionToOriginalState(this.question);
        (this.questionReview as any) = _.cloneDeep(
            this.commonQuestionnaireService.initialReviewQuestions.find((x) => x.id === this.question.id)?.cifc_review,
        );

        this.question.cifc_review = _.cloneDeep(this.questionReview) || ({} as CifcReview);
        this.commonQuestionnaireService.cancelReviewSubject.next(this.question);
        this.customerQuestionnaireService.removeQuestionFromReview(this.question.id);
        this.toastService.showError('Review was cancelled.', { duration: 3000 });
        this.commonQuestionnaireService.displayOkCifc[this.questionIndex] = false;
        this.cdr.detectChanges();
    }

    public removeAttachment() {
        this.currentAttachment = { file: null };
        (this.currentQuestion.body as any).attachement = { file: null };
    }

    public isSaveButtonDisabled(): boolean {
        return <boolean>(
            (this.cstQuestionnaireService.isLoading ||
                this.customerQuestionnaireService.isLoadingSubmit ||
                !this.showOk ||
                (!this.commonQuestionnaireService.createMode && !this.commonQuestionnaireService.reviewMode && !this.showOk) ||
                (this.commonQuestionnaireService.reviewMode &&
                    !this.commonQuestionnaireService.showReviewWarningMark(this.question as unknown as QuestionResponse) &&
                    !this.commonQuestionnaireService.displayOkCifc[this.questionIndex]) ||
                (this.commonQuestionnaireService.reviewMode &&
                    this.commonQuestionnaireService.showPendingMark(this.question as unknown as QuestionResponse) &&
                    !this.commonQuestionnaireService.displayOkCifc[this.questionIndex]))
        );
    }

    private addAttachmentToQuestion(file: File): void {
        if (!this.mimeTypesAllowed.includes(file.type)) {
            this.toastService.showError($localize`File type not supported`, {
                duration: 5000,
            });
            return;
        }

        if (file.size > this.maxSize * 1024 * 1024) {
            this.toastService.showError($localize`File size exceeds ${this.maxSize} MB`, { duration: 5000 });
            return;
        }
        this.currentAttachment = { file: file };
        (this.currentQuestion.body as any).attachement = { file: file };
    }
}
